import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaCheckCircle, FaBolt, FaCalendarCheck, FaUserCheck, FaStar, FaClock, FaShieldAlt, FaDollarSign, FaMapMarkerAlt, FaFire } from 'react-icons/fa';
import { trackEvent } from '../utils/analytics';
import { API_URL } from '../services/api';
import SEOHead from '../components/SEOHead';
import { RootState } from '../store';
import { getAccountAwarePlanCta } from '../utils/accountAwareCta';

type ActiveMarket = {
  key: string;
  name: string;
};

const FALLBACK_ACTIVE_MARKETS: ActiveMarket[] = [
  { name: 'Los Angeles', key: 'los-angeles' },
  { name: 'Inland Empire', key: 'inland-empire' },
  { name: 'Atlanta', key: 'atlanta' },
  { name: 'Dallas', key: 'dallas' },
  { name: 'Miami', key: 'miami' },
  { name: 'Nashville', key: 'nashville' },
  { name: 'Orlando', key: 'orlando' },
  { name: 'Orange County', key: 'orange-county' },
  { name: 'San Francisco', key: 'san-francisco' },
];

const WorkerLandingPage = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const utmSource = searchParams.get('utm_source') || '';
  const [liveStats, setLiveStats] = useState<{
    markets: number;
    activeMarkets: ActiveMarket[];
  } | null>(null);

  // Pass UTM params through to registration
  const registerUrl = utmSource
    ? `/register?role=worker&${searchParams.toString()}`
    : '/register?role=worker';
  const automationRegisterUrl = `${registerUrl}&redirect=%2Fsubscription`;
  const freeCta = getAccountAwarePlanCta({
    isAuthenticated,
    role: user?.role,
    kind: 'worker-free',
    signedOutLabel: 'Get Started Free',
    signedOutPath: registerUrl,
  });
  const automationCta = getAccountAwarePlanCta({
    isAuthenticated,
    role: user?.role,
    kind: 'worker-automation',
    signedOutLabel: 'Start Early Access',
    signedOutPath: automationRegisterUrl,
  });

  useEffect(() => {
    if (utmSource) {
      trackEvent('utm_landing', 'acquisition', `${utmSource}/${searchParams.get('utm_medium') || ''}/${searchParams.get('utm_campaign') || ''}/${searchParams.get('utm_content') || ''}`);
    }
    // Fetch live job stats for social proof banner
    fetch(`${API_URL}/api/scraped-jobs/markets`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && Array.isArray(data)) {
          const activeMarkets = data.filter(
            (market: { jobCount?: number }) => Number(market.jobCount) > 0,
          );
          setLiveStats({
            markets: activeMarkets.length,
            activeMarkets: activeMarkets.map((market: { key: string; name: string }) => ({
              key: market.key,
              name: market.name,
            })),
          });
        }
      })
      .catch(() => {}); // Fail silently — banner just won't show
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <SEOHead
        title="Find AV & Stagehand Gigs | StageCrew for Workers"
        description="Browse sourced AV technician, stagehand, and live-event listings across major US markets. Create a free profile and view relevant matches in StageCrew."
        url="https://stagecrew.app/for-workers"
        keywords="stagehand jobs, AV technician gigs, freelance stagehand, live event crew jobs, stagehand work near me, audio engineer gigs, lighting tech jobs, rigger jobs"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'StageCrew for Workers',
          url: 'https://stagecrew.app/for-workers',
          description: 'Browse sourced AV technician, stagehand, and live-event listings. Create a free profile and save your work preferences.',
          mainEntity: {
            '@type': 'Service',
            name: 'StageCrew Worker Platform',
            serviceType: 'Job Matching for Live Events Crew',
            areaServed: {
              '@type': 'Country',
              name: 'United States',
            },
            provider: {
              '@type': 'Organization',
              name: 'StageCrew',
              url: 'https://stagecrew.app',
            },
          },
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-700 via-teal-600 to-teal-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {liveStats && liveStats.markets > 0 && (
                <div className="inline-flex items-center gap-2 bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-1.5 rounded-full mb-4">
                  <FaFire /> Pay-disclosed listings across {liveStats.markets} markets
                </div>
              )}
              <div className="inline-block bg-white bg-opacity-20 text-sm font-bold px-4 py-1.5 rounded-full mb-6">
                Free to Join — No Worker Commission
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Find Better AV &amp;<br />Stagehand Gigs
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-teal-100 max-w-xl">
                Set your role, city, and rate once. StageCrew brings relevant live-event gigs together and shows matching work in your account.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={registerUrl}
                  onClick={() => trackEvent('worker_cta_click', 'conversion', 'hero')}
                  className="bg-white text-teal-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg text-center"
                >
                  Create Your Free Profile
                </Link>
                <a
                  href="#how-it-works"
                  className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition text-center"
                >
                  See How It Works
                </a>
              </div>
              <p className="mt-6 text-teal-200 text-sm">
                Free profile. No credit card. Role, city, and rate are enough to start.
              </p>
            </div>

            {/* Mock Worker Profile Card */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200 text-gray-900">
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-4">
                  Example worker dashboard
                </p>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-teal-100 text-teal-700 rounded-full w-14 h-14 flex items-center justify-center font-bold text-xl">
                    AJ
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Alex Johnson</h3>
                    <p className="text-sm text-gray-500">Audio Engineer & Stagehand</p>
                  </div>
                  <span className="ml-auto bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">Available</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['Audio', 'Lighting', 'Rigging', 'Dante', 'Line Arrays'].map((skill) => (
                    <span key={skill} className="bg-teal-50 text-teal-700 text-xs font-medium px-2.5 py-1 rounded-full border border-teal-200">
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Concert Load-In — Las Vegas</p>
                      <p className="text-xs text-gray-500">Apr 15 &middot; $55/hr</p>
                    </div>
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded-full">Saved</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Corporate AV Setup — LA</p>
                      <p className="text-xs text-gray-500">Apr 18 &middot; $45/hr</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full">Recommended</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Festival Rigging — San Diego</p>
                      <p className="text-xs text-gray-500">Apr 22-24 &middot; $500/day</p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full">New</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500">Example: 3 relevant gigs</span>
                  <span className="text-teal-700 font-semibold">Illustrative data</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600">Pay shown</div>
              <div className="text-gray-600 text-sm mt-1">On Every Public Listing</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">{liveStats?.markets ?? '—'}</div>
              <div className="text-gray-600 text-sm mt-1">Markets With Listings</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">$0</div>
              <div className="text-gray-600 text-sm mt-1">Platform Fees</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600">100%</div>
              <div className="text-gray-600 text-sm mt-1">Of Your Pay Kept</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Start Finding Gigs in 3 Steps</h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Use one focused job board for live-event work, then decide which opportunities are right for you.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaUserCheck className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 1
              </div>
              <h3 className="text-xl font-bold mb-3">Create a Basic Profile</h3>
              <p className="text-gray-600">
                Choose your primary role, city, and minimum rate. Add certifications, schedule, and media later.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaBolt className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 2
              </div>
              <h3 className="text-xl font-bold mb-3">Browse Relevant Gigs</h3>
              <p className="text-gray-600">
                Search current paid listings by market and role. Every public listing includes a structured amount and pay basis.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-teal-100 text-teal-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaCalendarCheck className="text-3xl" />
              </div>
              <div className="bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 3
              </div>
              <h3 className="text-xl font-bold mb-3">Save or Apply</h3>
              <p className="text-gray-600">
                Review each opportunity and apply on your terms. In-app match updates help you find newly added work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After — makes pain tangible */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Focus on Paid, Current Listings.</h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            You know the hustle. Here's what changes when you join StageCrew.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-8 border border-red-200">
              <h3 className="text-lg font-bold text-red-600 mb-6">Without StageCrew</h3>
              <ul className="space-y-4">
                {[
                  'Texting 20 contacts hoping someone has a call',
                  'Comparing scattered posts with missing pay or unclear status',
                  'Empty weeks followed by double-booked weekends',
                  'Chasing invoices for weeks after the gig',
                  'No way to prove your track record to new clients',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-600">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">&#10007;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 border-2 border-teal-500">
              <h3 className="text-lg font-bold text-teal-600 mb-6">With StageCrew</h3>
              <ul className="space-y-4">
                {[
                  'Paid listings from StageCrew employers and supported employer boards',
                  'One profile with your saved work preferences',
                  'In-app match updates when opportunities are added',
                  'Posting details and application links in one place',
                  'Optional profile details you can add when ready',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-700">
                    <FaCheckCircle className="text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why StageCrew for Workers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Why Crew Members Choose StageCrew</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaCalendarCheck className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Find Better-Fit Gigs</h3>
              <p className="text-gray-600">
                Browse live-event work in active markets and focus on roles that fit your experience.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaDollarSign className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Rate, Your Terms</h3>
              <p className="text-gray-600">
                Save your minimum rate and travel preferences once, then use them when reviewing opportunities.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaStar className="text-3xl text-teal-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Build a Useful Profile</h3>
              <p className="text-gray-600">
                Keep your roles, certifications, availability, and work links together for future applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Industry Trust */}
      <section className="py-16 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Current StageCrew Inventory</h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            StageCrew isn't a generic gig app. We're built specifically for AV, staging, and production crew.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-teal-600 mb-2">Pay shown</div>
              <div className="text-gray-600 text-sm">On Every Public Listing</div>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-teal-600 mb-2">{liveStats?.markets ?? '—'}</div>
              <div className="text-gray-600 text-sm">Markets With Listings</div>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-teal-600 mb-2">$0</div>
              <div className="text-gray-600 text-sm">Worker Platform Fees</div>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
              <div className="text-gray-600 text-sm">Of Your Pay Stays Yours</div>
            </div>
          </div>
          <div className="mt-12 bg-teal-50 rounded-xl p-8 max-w-3xl mx-auto">
            <p className="text-lg text-gray-700 italic text-center">
              "I've worked gigs, run crews, and dealt with the same broken hiring process everyone complains about. StageCrew is the tool I wish existed when I was freelancing."
            </p>
            <p className="text-center mt-4 font-semibold text-teal-700">— Edwin, Founder</p>
          </div>
        </div>
      </section>

      {/* Roles & Markets */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold mb-4">Gigs for Every Skill</h2>
              <p className="text-gray-600 mb-6">
                Whether you're a veteran gaffer or just starting as a stagehand, there are gigs for your skill level.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  'Stagehand', 'Audio Technician', 'Lighting Tech', 'Gaffer', 'Key Grip', 'Grip',
                  'Rigger', 'Camera Operator', 'LED Tech', 'AV Technician',
                  'Stage Manager', 'Production Assistant', 'Sound Engineer', 'Projectionist',
                ].map((role) => (
                  <span
                    key={role}
                    className="bg-teal-50 text-teal-700 border border-teal-200 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4">
                {liveStats ? 'Markets With Listings' : 'Explore StageCrew Markets'}
              </h2>
              <p className="text-gray-600 mb-6">
                {liveStats
                  ? 'This list updates from the current StageCrew job inventory.'
                  : 'Inventory counts are unavailable right now, so these links open StageCrew market guides.'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(liveStats ? liveStats.activeMarkets : FALLBACK_ACTIVE_MARKETS).map((city) => (
                  <Link key={city.key} to={`/gigs/${city.key}`} className="flex items-center gap-2 text-gray-700 hover:text-teal-600 transition">
                    <FaMapMarkerAlt className="text-teal-500 flex-shrink-0" />
                    <span className="font-medium">{city.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Simple Worker Pricing</h2>
          <p className="text-center text-gray-600 mb-4 text-lg">
            Browse gigs for free. Join early access for optional application automation.
          </p>
          <p className="text-center text-gray-500 text-sm mb-4">
            Automation attempts are available only where the destination supports submission.
          </p>
          <p className="text-center text-green-600 font-semibold mb-12">
            No platform fees — you keep 100% of what you earn.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <FaShieldAlt className="text-2xl text-gray-500 mb-3" />
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-gray-500 ml-2">/forever</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Browse all gigs</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Create your profile</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Save your work preferences</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>In-app match updates</span>
                </li>
              </ul>
              <Link
                to={freeCta.path}
                className="block w-full text-center bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition"
              >
                {freeCta.label}
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-teal-600">
              <FaClock className="text-2xl text-teal-600 mb-3" />
              <h3 className="text-2xl font-bold mb-2">Automation Early Access</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-bold">$9</span>
                <span className="text-gray-500 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Everything in Free</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span className="font-medium">Up to 20 supported automation attempts/week</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Review-first and pause controls</span>
                </li>
              </ul>
              <Link
                to={automationCta.path}
                className="block w-full text-center bg-teal-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-teal-700 transition"
              >
                {automationCta.label}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Worker FAQ</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Is StageCrew really free to join?',
                a: 'Yes. Creating a profile, browsing current listings, matching, and applying manually are free. The optional $9 monthly Automation Early Access plan adds up to 20 supported attempts per week.',
              },
              {
                q: 'How does auto-apply work?',
                a: 'When enabled, StageCrew uses your saved role, rate, and location preferences to find relevant postings and submit where the destination supports it. You can keep automation paused or choose review-first mode.',
              },
              {
                q: 'What kind of gigs are on StageCrew?',
                a: 'Live events — concerts, corporate events, trade shows, film/TV, theater, and installs. Roles include stagehands, audio engineers, lighting techs, riggers, camera ops, gaffers, grips, and more.',
              },
              {
                q: 'Do I need to be IATSE or union?',
                a: 'No. StageCrew supports both union (IATSE, IBEW) and non-union workers. You can set your union status on your profile, and employers can filter by preference.',
              },
              {
                q: 'Which markets does StageCrew track?',
                a: 'StageCrew publishes supported market pages, and the market list above reflects listings currently shown on StageCrew. Coverage and listing volume change as sources update.',
              },
              {
                q: 'How do I get paid?',
                a: 'Payment terms and timing are handled between you and the employer. StageCrew connects you and does not take a cut of your earnings.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{q}</h3>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/for-workers/faq" className="text-green-600 font-semibold hover:underline">
              View all worker FAQs &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Questions? We're Crew Too.</h2>
          <p className="text-gray-600 text-lg mb-8">
            Not sure if StageCrew is right for you? Email Edwin directly — he's worked gigs and gets it.
          </p>
          <a
            href="mailto:edwin@stagecrew.app?subject=StageCrew%20Worker%20Question"
            className="inline-block bg-teal-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition shadow-lg"
          >
            Email Us: edwin@stagecrew.app
          </a>
          <p className="mt-4 text-gray-500 text-sm">We read every message.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Browse Current Listings?</h2>
          <p className="text-xl mb-8 text-teal-100 max-w-2xl mx-auto">
            Create a basic profile, save your preferences, and choose the opportunities that fit. No fees, no contracts.
          </p>
          <Link
            to={registerUrl}
            className="bg-white text-teal-700 px-12 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg inline-block"
          >
            Create Your Free Profile
          </Link>
          <p className="mt-4 text-teal-200 text-sm">
            Already have an account? <Link to="/login" className="underline hover:text-white">Log in</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default WorkerLandingPage;
